import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isAdminAuthenticated } from '@/integrations/php/client';
import GuestLeadView from '@/components/leads/GuestLeadView';

const accessStorageKey = (leadId: string) => `partner-lead-access:${leadId}`;

const readLeadAccessToken = (leadId: string): string => {
  const hash = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const tokenFromLink = new URLSearchParams(hash).get('access') ?? '';
  const isWellFormed = /^v1\.[0-9]{10}\.[A-Za-z0-9_-]{43}$/.test(tokenFromLink);

  if (isWellFormed) {
    sessionStorage.setItem(accessStorageKey(leadId), tokenFromLink);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}`);
    return tokenFromLink;
  }

  return sessionStorage.getItem(accessStorageKey(leadId)) ?? '';
};

/**
 * A scoped SMS/email bearer link renders one lead without login. Plain legacy
 * links still route an existing session to its portal and otherwise to login.
 */
const LeadDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [accessToken] = useState(() => (id ? readLeadAccessToken(id) : ''));

  useEffect(() => {
    if (accessToken) return;

    const routeAuthenticatedUser = async () => {
      if (!id) {
        navigate('/partner/login', { replace: true });
        return;
      }

      try {
        if (await isAdminAuthenticated()) {
          navigate(`/admin/lead/${encodeURIComponent(id)}`, { replace: true });
          return;
        }

        const response = await fetch('/api/partner-session.php', { credentials: 'include' });
        const session = await response.json().catch(() => ({}));
        if (response.ok && session.is_partner && session.partner_id) {
          navigate(
            `/partner/${encodeURIComponent(session.partner_id)}/leads?lead=${encodeURIComponent(id)}`,
            { replace: true },
          );
          return;
        }
      } catch {
        // The login screen is the safe fallback for an absent/expired session.
      }

      const next = `${location.pathname}${location.search}`;
      navigate(`/partner/login?next=${encodeURIComponent(next)}`, { replace: true });
    };

    void routeAuthenticatedUser();
  }, [accessToken, id, location.pathname, location.search, navigate]);

  if (id && accessToken) {
    return <GuestLeadView accessToken={accessToken} />;
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <Loader2 className="h-8 w-8 animate-spin text-primary" />
      <span className="sr-only">Έλεγχος πρόσβασης</span>
    </div>
  );
};

export default LeadDetails;
